import { useLocation, useHistory } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { DataGridFilter } from './DataGrid.types';

export interface FilterValue {
  key: string;
  value: unknown;
}

export interface UseUrlFiltersOptions {
  availableFilters: DataGridFilter<any>[];
  appliedFilters: FilterValue[];
  setFilterValues: (filters: FilterValue[]) => void;
}

export interface UseUrlPageOptions {
  page: number;
  setPage: (page: number) => void;
}

export function useUrlPage({ page, setPage }: UseUrlPageOptions) {
  const location = useLocation();
  const history = useHistory();
  const isUrlUpdate = useRef(false);

  const updateUrl = (newPage: number) => {
    const urlParams = new URLSearchParams(location.search);

    if (newPage === 1) {
      urlParams.delete('page');
    } else {
      urlParams.set('page', String(newPage));
    }

    const newUrl = `${location.pathname}?${urlParams.toString()}`;
    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlPage = urlParams.get('page');

    if (urlPage) {
      const pageNumber = parseInt(urlPage, 10);
      if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber !== page) {
        isUrlUpdate.current = true;
        setPage(pageNumber);
      }
    } else if (page !== 1) {
      isUrlUpdate.current = true;
      setPage(1);
    }
  }, [location.search]);

  useEffect(() => {
    if (!isUrlUpdate.current) {
      const urlParams = new URLSearchParams(location.search);
      const urlPage = urlParams.get('page');
      const currentUrlPage = urlPage ? parseInt(urlPage, 10) : 1;

      if (page !== currentUrlPage) {
        updateUrl(page);
      }
    } else {
      isUrlUpdate.current = false;
    }
  }, [page]);

  return {
    updateUrl,
  };
}

export function useUrlFilters({
  availableFilters,
  appliedFilters,
  setFilterValues,
}: UseUrlFiltersOptions) {
  const location = useLocation();
  const history = useHistory();

  const parseFiltersFromUrl = (): FilterValue[] => {
    const urlParams = new URLSearchParams(location.search);
    const filters: FilterValue[] = [];

    availableFilters.forEach((filter) => {
      const value = urlParams.get(filter.key);
      if (value) {
        const values = value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        values.forEach((val) => {
          filters.push({ key: filter.key, value: val });
        });
      }
    });

    return filters;
  };

  const areFiltersExplicitlyCleared = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('filter') === '';
  };

  const getDefaultFilters = () => {
    return availableFilters.flatMap((filter) =>
      filter.options
        .filter((option) => option.appliedByDefault)
        .map((option) => ({
          key: filter.key,
          value: option.value,
        })),
    );
  };

  const updateUrl = (filters: FilterValue[], wasCleared = false) => {
    const urlParams = new URLSearchParams(location.search);

    availableFilters.forEach((filter) => {
      urlParams.delete(filter.key);
    });
    urlParams.delete('filter');

    if (filters.length === 0 && wasCleared) {
      urlParams.set('filter', '');
    } else if (filters.length > 0) {
      const filterGroups = filters.reduce((acc, filter) => {
        if (!acc[filter.key]) {
          acc[filter.key] = [];
        }
        acc[filter.key].push(String(filter.value));
        return acc;
      }, {} as Record<string, string[]>);

      Object.entries(filterGroups).forEach(([key, values]) => {
        urlParams.set(key, values.join(','));
      });
    }

    const newUrl = `${location.pathname}?${urlParams.toString()}`;
    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  };

  const clearAllFilters = () => {
    setFilterValues([]);
    updateUrl([], true);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlFilters: FilterValue[] = [];

    availableFilters.forEach((filter) => {
      const value = urlParams.get(filter.key);
      if (value) {
        const values = value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        values.forEach((val) => {
          urlFilters.push({ key: filter.key, value: val });
        });
      }
    });

    const defaultFilters = availableFilters.flatMap((filter) =>
      filter.options
        .filter((option) => option.appliedByDefault)
        .map((option) => ({
          key: filter.key,
          value: option.value,
        })),
    );

    const isExplicitlyCleared = urlParams.get('filter') === '';

    if (urlFilters.length > 0) {
      setFilterValues(urlFilters);
    } else if (!isExplicitlyCleared && defaultFilters.length > 0) {
      setFilterValues(defaultFilters);
    }
  }, [location.search, availableFilters, setFilterValues]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlFilters: FilterValue[] = [];

    availableFilters.forEach((filter) => {
      const value = urlParams.get(filter.key);
      if (value) {
        const values = value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        values.forEach((val) => {
          urlFilters.push({ key: filter.key, value: val });
        });
      }
    });

    const filtersMatch =
      appliedFilters.length === urlFilters.length &&
      appliedFilters.every((filter) =>
        urlFilters.some(
          (urlFilter) =>
            urlFilter.key === filter.key && urlFilter.value === filter.value,
        ),
      );

    if (!filtersMatch) {
      const urlParams = new URLSearchParams(location.search);

      availableFilters.forEach((filter) => {
        urlParams.delete(filter.key);
      });
      urlParams.delete('filter');

      if (appliedFilters.length === 0) {
        urlParams.set('filter', '');
      } else {
        const filterGroups = appliedFilters.reduce((acc, filter) => {
          if (!acc[filter.key]) {
            acc[filter.key] = [];
          }
          acc[filter.key].push(String(filter.value));
          return acc;
        }, {} as Record<string, string[]>);

        Object.entries(filterGroups).forEach(([key, values]) => {
          urlParams.set(key, values.join(','));
        });
      }

      const newUrl = `${location.pathname}?${urlParams.toString()}`;
      if (newUrl !== `${location.pathname}${location.search}`) {
        history.replace(newUrl);
      }
    }
  }, [
    appliedFilters,
    location.pathname,
    location.search,
    history,
    availableFilters,
  ]);

  return {
    clearAllFilters,
    parseFiltersFromUrl,
  };
}
